import {
  Divider, Page, Spinner, IconButton,
  IconButtonType
} from "@whitespace/whitespace-ui";
import { updateCreateItemOrderPageOrigination } from "actions/updateCreateItemOrderPageDetails";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useStateMachine } from "little-state-machine";
import {
  Button, Card, Row, Col, Container
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useState, useEffect, useCallback } from "react";
import { parseUsingParseInt } from "helpers/stringUtils";
import "styles/sf-datagrid.scss";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import IItemOrderStatusViewModel from "interfaces/IItemOrderStatusViewModel";
import IGetItemOrdersResponseModel from "interfaces/Response/IGetItemOrdersResponseModel";
import DetailsTemplate from "./Templates/DetailsTemplate";
import EditTemplate from "./Templates/EditTemplate";
import LocationTemplate from "./Templates/LocationTemplate";
import OrderItemTemplate from "./Templates/OrderItemTemplate";
import RecipientTemplate from "./Templates/RecipientTemplate";
import StatusTemplate from "./Templates/StatusTemplate";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const filterSettings: FilterSettingsModel = {
  type: "Excel"
};

let grid: GridComponent | undefined;

const ItemOrderStatuses = () => {
  const [renderingComplete, setRenderingComplete] = useState(false);
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [itemOrderViewModels, setItemOrderViewModels] = useState<IItemOrderStatusViewModel[] | undefined>([]);

  const detailsTemplateInstance = DetailsTemplate;
  const editTemplateInstance = EditTemplate;
  const locationTemplateInstance = LocationTemplate;
  const orderItemTemplateInstance = OrderItemTemplate;
  const recipientTemplateInstance = RecipientTemplate;
  const statusTemplateInstance = StatusTemplate;

  const { actions } = useStateMachine({
    updateCreateItemOrderPageOrigination
  });
  const navigate = useNavigate();

  const navigateToCreateItemOrder = () => {
    actions.updateCreateItemOrderPageOrigination(RoutePathsEnum.ItemOrders);
    navigate(RoutePathsEnum.ItemOrdersCreate);
  };
  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  useEffect(() => {
    sendRequest("itemorders", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && (data as IGetItemOrdersResponseModel).itemOrders) {
      const itemOrderStatuses = data.itemOrders as IItemOrderStatusViewModel[];
      setItemOrderViewModels(itemOrderStatuses);
    }
  }, [data, setItemOrderViewModels]);

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="item-order-management">
              Order Details
              <Spinner
                animation="grow"
                visible={isSending || !renderingComplete}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          <div className="float-end">
            <IconButton
              data-testid="add-item-order"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              onClick={navigateToCreateItemOrder}
            >
              Add New Order
            </IconButton>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <section className="regions pt-2">
            <Container fluid>
              <Row>
                <Col>
                  <div className="control-pane">
                    <div className="control-section">
                      <GridComponent
                        ref={(g: GridComponent) => {
                          grid = g;
                        }}
                        id="overviewgrid"
                        data-testid="itemOrderStatuses"
                        allowPaging
                        allowResizing
                        pageSettings={pageSettings}
                        allowSorting
                        allowFiltering
                        filterSettings={filterSettings}
                        allowSelection={false}
                        dataSource={itemOrderViewModels}
                        actionComplete={() => {
                          makeGridAACompliant(grid!, () => {
                            setRenderingComplete(true);
                          });
                        }}
                        loadingIndicator={{ indicatorType: "Shimmer" }}
                        type=""
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="id"
                            headerText="id"
                            visible={false}
                            isPrimaryKey
                          />
                          <ColumnDirective
                            field="hwrcSiteName"
                            headerText="Location"
                            template={locationTemplateInstance}
                          />
                          <ColumnDirective
                            field="orderType"
                            headerText="Order Item"
                            template={orderItemTemplateInstance}
                            width={70}
                          />
                          <ColumnDirective
                            field="item"
                            headerText="Details"
                            template={detailsTemplateInstance}
                            hideAtMedia="(min-width:800px)"
                          />
                          <ColumnDirective
                            field="recipient"
                            headerText="Recipient"
                            template={recipientTemplateInstance}
                            width={70}
                            hideAtMedia="(min-width:1200px)"
                          />
                          <ColumnDirective
                            field="status"
                            headerText="Status"
                            headerTextAlign="Center"
                            template={statusTemplateInstance}
                            width={70}
                            type="string"
                          />
                          <ColumnDirective
                            headerText=""
                            allowSorting={false}
                            allowResizing={false}
                            width={30}
                            template={editTemplateInstance}
                            allowFiltering={false}
                            minWidth="30px"
                          />
                        </ColumnsDirective>
                        <Inject services={[Filter, Paging, Sort, Resize]} />
                      </GridComponent>

                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ItemOrderStatuses;
